import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './ui/styles.css';
import './ui/cables.css';

const root = document.getElementById('root');
if (!root) throw new Error('Root element missing from index.html');

/**
 * StrictMode is deliberately NOT used here.
 *
 * It double-invokes effects in development, which would create and tear down a
 * second AudioContext and a second set of AudioWorklet nodes on every mount.
 * That is exactly the kind of graph churn this application exists to avoid, and
 * debugging audio glitches that only occur in development is a waste of
 * everyone's time. Component purity is maintained by discipline instead.
 */
createRoot(root).render(<App />);

/**
 * Release the AudioContext when the page goes away.
 *
 * Browsers cap the number of live AudioContexts per page, and a context that is
 * never closed survives a hot reload. Without this, a development session with
 * many reloads eventually exhausts the limit and the next context creation
 * hangs -- which looks exactly like an application bug and is not one.
 */
const releaseAudio = () => {
  void import('./audio/engine').then(({ engine }) => {
    void engine.teardown();
  });
};
window.addEventListener('pagehide', releaseAudio);
if (import.meta.hot) import.meta.hot.dispose(releaseAudio);

// Surface anything that escapes React so it lands in the debug console rather
// than only in devtools.
window.addEventListener('error', (e) => {
  void import('./stores/appStore').then(({ useApp }) => {
    useApp.getState().log('error', `Uncaught: ${e.message} (${e.filename}:${e.lineno})`);
  });
});
window.addEventListener('unhandledrejection', (e) => {
  void import('./stores/appStore').then(({ useApp }) => {
    useApp.getState().log('error', `Unhandled promise rejection: ${String((e.reason as Error)?.message ?? e.reason)}`);
  });
});

/**
 * Debug handle.
 *
 * Exposed deliberately and permanently. This is laboratory software: being able
 * to drive the store, inspect the compiled payloads and query the registry from
 * a console is a feature, and it is also how the acceptance workflow is
 * verified. Nothing here grants access the page does not already have.
 */
void Promise.all([
  import('./stores/appStore'),
  import('./audio/engine'),
  import('./database'),
  import('./dsp/compile'),
]).then(([store, eng, db, dsp]) => {
  (window as unknown as Record<string, unknown>).dacEngine = {
    store: store.useApp,
    engine: eng.engine,
    registry: db.registry,
    checkIntegrity: db.checkIntegrity,
    compileSystem: dsp.compileSystem,
    version: '1.0.0',
  };
});

export { StrictMode };
